import React, {useEffect, useMemo, useRef, useState} from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

function estimateSectionHeight(content: string): number {
    const lineCount = content.split('\n').length;
    return Math.max(lineCount * 24, 120);
}

interface MarkdownSectionProps {
    content: string;
    estimatedHeight: number;
}

const MarkdownSection = React.memo(function MarkdownSection({content, estimatedHeight}: MarkdownSectionProps) {
    const ref = useRef<HTMLDivElement>(null);
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        const el = ref.current;
        if (!el) return () => undefined;

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                    observer.disconnect();
                }
            },
            {rootMargin: '300px'}
        );

        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    return (
        <div ref={ref}>
            {isVisible ? (
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
            ) : (
                <div style={{minHeight: estimatedHeight}} />
            )}
        </div>
    );
});

interface MarkdownPreviewProps {
    content: string;
}

export function MarkdownPreview({content}: MarkdownPreviewProps): JSX.Element {
    const sections = useMemo(() => content.split(/(?=\n?^#{1,2} )/m).filter((s) => s.trim().length > 0), [content]);

    return (
        <div className="c-generatorPage__content_preview">
            {sections.map((section, index) => (
                <MarkdownSection key={index} content={section} estimatedHeight={estimateSectionHeight(section)} />
            ))}
        </div>
    );
}
